import React, { useState, useEffect, useRef } from 'react';
import jimmyImg from '../assets/Jimmy.png';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import { LineChart, Line, ComposedChart, Bar, Legend, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer } from 'recharts';
import { fetchVipStats, fetchVipGeoData, fetchVipMembers, fetchBestVipCustomers, neonQuery, neonRawSelect } from '../services/api';
import { getCurrentUser } from '../services/auth';
import useIsMobile from '../hooks/useIsMobile';
import { toTitleCase } from '../utils/format';

const DD_PROXY = 'https://crabplace-dotdigital.netlify.app/.netlify/functions';
const SYNC_KEY = 'vip_dd_last_sync';
const SYNC_EXPIRED_KEY = 'vip_dd_expired_last_sync';

const GOAL = 20000;
const GOAL_DATE = 'Mar 31, 2026';
const ORDERS_GOAL = 3.61;
const PAGE_SIZE = 25;

// ── VIP net growth ──────────────────────────────────────────────────────────
// Windows Greg can flip between. 'all' reaches back to the first membership order
// in 2004; the club's own history starts there.
const GROWTH_WINDOWS = [
  { key: '12m', label: '12 months', months: 12,  grain: 'month' },
  { key: '3y',  label: '3 years',   months: 36,  grain: 'month' },
  { key: '5y',  label: '5 years',   months: 60,  grain: 'month' },
  { key: 'all', label: 'All time',  months: null, grain: 'year' },
];
const GROWTH_DEFAULT = '3y';

function growthSince(key) {
  const w = GROWTH_WINDOWS.find(x => x.key === key) || GROWTH_WINDOWS[1];
  if (!w.months) return '2004-01-01';
  const d = new Date();
  d.setMonth(d.getMonth() - w.months);
  return d.toISOString().split('T')[0];
}

// Auto-renew opt-outs have no timestamp at source, so historical ones are dated to the
// lapse they explain and pile onto March 31. Only live-captured ones are a real daily
// signal — the chart says so rather than letting the spike be read as real.
const OPTOUT_TRACKING_NOTE =
  'Opt-out dates before live capture are anchored to the lapse they caused, not the day the member decided.';

const GEO_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';

const NAME_TO_ABBR = {
  'Alabama':'AL','Alaska':'AK','Arizona':'AZ','Arkansas':'AR','California':'CA',
  'Colorado':'CO','Connecticut':'CT','Delaware':'DE','District of Columbia':'DC',
  'Florida':'FL','Georgia':'GA','Hawaii':'HI','Idaho':'ID','Illinois':'IL',
  'Indiana':'IN','Iowa':'IA','Kansas':'KS','Kentucky':'KY','Louisiana':'LA',
  'Maine':'ME','Maryland':'MD','Massachusetts':'MA','Michigan':'MI','Minnesota':'MN',
  'Mississippi':'MS','Missouri':'MO','Montana':'MT','Nebraska':'NE','Nevada':'NV',
  'New Hampshire':'NH','New Jersey':'NJ','New Mexico':'NM','New York':'NY',
  'North Carolina':'NC','North Dakota':'ND','Ohio':'OH','Oklahoma':'OK','Oregon':'OR',
  'Pennsylvania':'PA','Rhode Island':'RI','South Carolina':'SC','South Dakota':'SD',
  'Tennessee':'TN','Texas':'TX','Utah':'UT','Vermont':'VT','Virginia':'VA',
  'Washington':'WA','West Virginia':'WV','Wisconsin':'WI','Wyoming':'WY',
};

const STATE_CENTROIDS = {
  AL:[-86.8,32.8],AK:[-153.4,64.2],AZ:[-111.7,34.3],AR:[-92.4,34.9],CA:[-119.6,37.2],
  CO:[-105.5,39.0],CT:[-72.7,41.6],DE:[-75.5,39.0],DC:[-77.0,38.9],FL:[-81.5,27.8],
  GA:[-83.4,32.7],HI:[-157.5,20.3],ID:[-114.5,44.4],IL:[-89.2,40.0],IN:[-86.3,40.0],
  IA:[-93.5,42.1],KS:[-98.4,38.5],KY:[-84.9,37.5],LA:[-91.8,31.2],ME:[-69.4,45.4],
  MD:[-76.6,39.0],MA:[-71.5,42.3],MI:[-85.4,44.3],MN:[-94.4,46.4],MS:[-89.7,32.7],
  MO:[-92.5,38.5],MT:[-109.6,47.0],NE:[-99.9,41.5],NV:[-116.4,39.3],NH:[-71.6,43.7],
  NJ:[-74.5,40.1],NM:[-106.2,34.5],NY:[-75.4,43.0],NC:[-79.4,35.6],ND:[-100.5,47.5],
  OH:[-82.8,40.4],OK:[-97.5,35.6],OR:[-120.5,44.0],PA:[-77.2,40.9],RI:[-71.5,41.7],
  SC:[-80.9,33.8],SD:[-100.0,44.4],TN:[-86.3,35.9],TX:[-99.3,31.5],UT:[-111.1,39.3],
  VT:[-72.7,44.0],VA:[-78.2,37.5],WA:[-120.5,47.4],WV:[-80.6,38.6],WI:[-89.6,44.5],
  WY:[-107.3,43.0],
};

const MAX_DOT_R = 26;

function heatColor(intensity) {
  if (!intensity) return '#e8eaed';
  const r = Math.round(0xf5 + (0xDC - 0xf5) * intensity);
  const g = Math.round(0xe8 + (0x58 - 0xe8) * intensity);
  const b = Math.round(0xd8 + (0x2A - 0xd8) * intensity);
  return `rgb(${r},${g},${b})`;
}

function downloadCsv(rows) {
  if (!rows?.length) return;
  const headers = Object.keys(rows[0]);
  const escape = v => {
    const s = v == null ? '' : String(v);
    return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [headers.join(','), ...rows.map(r => headers.map(h => escape(r[h])).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `jimmy-export-${new Date().toISOString().slice(0,10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function VipClub({ navigateTo }) {
  const user = getCurrentUser();
  const isMobile = useIsMobile();
  const showSync = (user?.staffName === 'Greg Cain' || user?.staffName === 'George Cain') && !isMobile;

  // Dashboard stats
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(null);

  // Geo / map
  const [geoData, setGeoData] = useState(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoOpen, setGeoOpen] = useState(false);
  const [geoView, setGeoView] = useState('table');
  const [showDots, setShowDots] = useState(true);
  const [mapScale, setMapScale] = useState(60);
  const [expandedStates, setExpandedStates] = useState(new Set());
  const [cityData, setCityData] = useState({});
  const [cityLoading, setCityLoading] = useState(new Set());
  const [tooltip, setTooltip] = useState(null);
  const [cityMembersCache, setCityMembersCache] = useState({});
  const [cityMembersTip, setCityMembersTip] = useState(null); // { state, city, x, y, members, loading }
  const [citySort, setCitySort] = useState('count'); // 'count' | 'alpha'
  // Dotdigital sync — not renewing
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(() => {
    try { return JSON.parse(localStorage.getItem(SYNC_KEY)); } catch { return null; }
  });
  // Dotdigital sync — expired
  const [syncingExpired, setSyncingExpired] = useState(false);
  const [syncProgressExpired, setSyncProgressExpired] = useState(null);
  const [syncResultExpired, setSyncResultExpired] = useState(() => {
    try { return JSON.parse(localStorage.getItem(SYNC_EXPIRED_KEY)); } catch { return null; }
  });

  const handleSync = async () => {
    setSyncing(true);
    try {
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
      const res = await fetch(`${DD_PROXY}/vip-not-renewing-sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cpToken: currentUser.accessToken }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      const result = { ...data, syncedAt: data.syncedAt || new Date().toISOString() };
      setSyncResult(result);
      localStorage.setItem(SYNC_KEY, JSON.stringify(result));
    } catch (e) {
      setSyncResult({ error: e.message, syncedAt: new Date().toISOString() });
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncExpired = async () => {
    setSyncingExpired(true);
    setSyncProgressExpired({ page: 0, added: 0, suppressed: 0 });
    const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
    let cursor = 0, bookId = null, totalVips = null, totalAdded = 0, totalSuppressed = 0, totalOther = 0, totalNoEmail = 0, lastSampleError = null;
    try {
      while (true) {
        const res = await fetch(`${DD_PROXY}/vip-expired-sync`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ cpToken: currentUser.accessToken, cursor, bookId, totalVips }),
        });
        const data = await res.json();
        if (data.error) throw new Error(data.error);
        bookId = data.bookId;
        totalVips = data.totalVips;
        cursor = data.cursor;
        totalAdded += data.added || 0;
        totalSuppressed += data.suppressed || 0;
        totalOther += data.otherErrors || 0;
        totalNoEmail += data.noEmail || 0;
        if (data.sampleOtherError) lastSampleError = data.sampleOtherError;
        setSyncProgressExpired({ cursor, totalVips, added: totalAdded, suppressed: totalSuppressed });
        if (data.done) break;
      }
      const result = { added: totalAdded, suppressed: totalSuppressed, otherErrors: totalOther, noEmail: totalNoEmail, sampleOtherError: lastSampleError, syncedAt: new Date().toISOString() };
      setSyncResultExpired(result);
      setSyncProgressExpired(null);
      localStorage.setItem(SYNC_EXPIRED_KEY, JSON.stringify(result));
    } catch (e) {
      setSyncResultExpired({ error: e.message, syncedAt: new Date().toISOString() });
      setSyncProgressExpired(null);
    } finally {
      setSyncingExpired(false);
    }
  };

  // Best VIP Customers
  const [bestOpen, setBestOpen] = useState(false);
  const [bestData, setBestData] = useState(null);
  const [bestLoading, setBestLoading] = useState(false);
  const [bestError, setBestError] = useState(null);

  const handleBestToggle = () => {
    if (!bestOpen && !bestData && !bestLoading) {
      setBestLoading(true);
      fetchBestVipCustomers(25)
        .then(setBestData)
        .catch(e => setBestError(e.message))
        .finally(() => setBestLoading(false));
    }
    setBestOpen(o => !o);
  };

  // ── Chat ────────────────────────────────────────────────────────────────────
  const [chatOpen, setChatOpen] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [displayMessages, setDisplayMessages] = useState([]);   // { role, text } for rendering
  const [apiMessages, setApiMessages] = useState([]);           // full Claude API format
  const chatEndRef = useRef(null);

  const CHAT_BASE = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions'
    : '/.netlify/functions';

  const toggleStateExpand = async (abbr) => {
    if (expandedStates.has(abbr)) {
      setExpandedStates(prev => { const s = new Set(prev); s.delete(abbr); return s; });
      return;
    }
    setExpandedStates(prev => new Set([...prev, abbr]));
    if (cityData[abbr]) return; // already loaded
    setCityLoading(prev => new Set([...prev, abbr]));
    try {
      const rows = await neonQuery('vip_cities_by_state', { state: abbr });
      setCityData(prev => ({ ...prev, [abbr]: rows }));
    } catch (e) {
      setCityData(prev => ({ ...prev, [abbr]: [] }));
    } finally {
      setCityLoading(prev => { const s = new Set(prev); s.delete(abbr); return s; });
    }
  };

  const ANALYTIC_TOOLS = ['top_n', 'distribution', 'segment_stats', 'group_by', 'cohort_compare', 'trend', 'search_customers', 'list_notable_customers'];

  const executeTool = async (name, input) => {
    const ok = (resultObj, exportRows = null) => ({
      result: JSON.stringify(resultObj),
      exportRows: exportRows?.length ? exportRows : null,
    });
    try {
      if (ANALYTIC_TOOLS.includes(name)) {
        const rows = await neonQuery('jimmy_analytics', { tool: name, args: input });
        return ok({ rowCount: rows.length, rows }, rows);
      }
      if (name === 'get_vip_stats') {
        const s = await fetchVipStats();
        return ok(s);
      }
      if (name === 'get_best_vip_customers') {
        const data = await fetchBestVipCustomers(input.topN || 25);
        const rows = Array.isArray(data) ? data : data?.customers ?? [];
        return ok(data, rows);
      }
      if (name === 'search_vip_members') {
        const params = {
          PageNumber: 1,
          PageSize: Math.min(input.pageSize || 20, 100),
          ...(input.firstName ? { FirstName: input.firstName, Filter_FirstName: true } : {}),
          ...(input.lastName  ? { LastName:  input.lastName,  Filter_LastName:  true } : {}),
          ...(input.email     ? { Email:     input.email,     Filter_Email:     true } : {}),
          ...(input.status    ? { Status:    input.status,    Filter_Status:    true } : {}),
          ...(input.renewal === 'auto-renew' ? { Renewal: 1 } : {}),
          ...(input.renewal === 'no-renew'   ? { Renewal: 0 } : {}),
        };
        const raw = await fetchVipMembers(params);
        const members = (raw?.vipMembers ?? []).map(m => ({
          name:        m.customerName,
          email:       m.customerEmail,
          joined:      m.joinedDate,
          expires:     m.expirationDate,
          status:      m.status === true ? 'Active' : 'Inactive',
          autoRenew:   m.doNotRenewal === false ? 'Yes' : 'No',
          hasPayment:  Boolean(m.customerPaymentProfileId),
        }));
        return ok({ total: raw?.totalRows ?? members.length, members }, members);
      }
      // ── Neon-powered tools ──────────────────────────────────────────────────
      if (name === 'get_vip_stats_neon') {
        const rows = await neonQuery('vip_stats_neon', {});
        return ok(rows[0] ?? {});
      }
      if (name === 'get_vip_top_spenders') {
        const rows = await neonQuery('vip_top_spenders', { limit: input.limit || 25, active: input.active !== false });
        const fmtCur = n => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        const fmtDate = d => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
        const mapped = rows.map(r => ({
          name: [r.first_name, r.last_name].filter(Boolean).join(' '),
          email: r.email,
          lifetimeValue: fmtCur(r.lifetime_value),
          orderCount: Number(r.order_count),
          avgOrderValue: fmtCur(r.avg_order_value),
          lastOrderDate: fmtDate(r.last_order_date),
          joinedDate: fmtDate(r.joined_date),
          active: r.active,
          autoRenew: r.auto_renew,
        }));
        return ok(mapped, mapped);
      }
      if (name === 'get_vip_lapsed') {
        const rows = await neonQuery('vip_lapsed', { days: input.days || 90, active: input.active !== false, limit: input.limit || 50 });
        const fmtDate = d => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Never';
        const mapped = rows.map(r => ({
          name: [r.first_name, r.last_name].filter(Boolean).join(' '),
          email: r.email,
          lastOrderDate: fmtDate(r.last_order_date),
          daysSinceLastOrder: r.last_order_date ? Math.round(Number(r.days_since_last_order)) : 'Never ordered',
          orderCount: Number(r.order_count),
          lifetimeValue: `$${Number(r.lifetime_value || 0).toFixed(2)}`,
          active: r.active,
          autoRenew: r.auto_renew,
          expirationDate: fmtDate(r.expiration_date),
        }));
        return ok(mapped, mapped);
      }
      if (name === 'get_vip_expiring') {
        const rows = await neonQuery('vip_expiring', { days: input.days || 30, limit: input.limit || 50 });
        const fmtDate = d => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
        const mapped = rows.map(r => ({
          name: [r.first_name, r.last_name].filter(Boolean).join(' '),
          email: r.email,
          expirationDate: fmtDate(r.expiration_date),
          autoRenew: r.auto_renew,
          hasPaymentProfile: r.has_payment_profile,
          orderCount: Number(r.order_count),
          lastOrderDate: fmtDate(r.last_order_date),
          lifetimeValue: `$${Number(r.lifetime_value || 0).toFixed(2)}`,
        }));
        return ok(mapped, mapped);
      }
      if (name === 'get_vip_order_history') {
        const rows = await neonQuery('vip_order_history', { q: input.q, limit: input.limit || 20 });
        const fmtDate = d => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
        const member = rows[0] ? {
          name: [rows[0].first_name, rows[0].last_name].filter(Boolean).join(' '),
          email: rows[0].email,
          joinedDate: fmtDate(rows[0].joined_date),
          expirationDate: fmtDate(rows[0].expiration_date),
          active: rows[0].active,
        } : null;
        const orders = rows.map(r => ({
          bcOrderId: r.bc_order_id ?? r.id,
          date: fmtDate(r.order_date),
          status: r.status,
          amount: `$${Number(r.order_amount || 0).toFixed(2)}`,
          shipMethod: r.ship_method,
          tracking: r.tracking_number,
        }));
        return ok({ member, orders }, orders);
      }
      if (name === 'get_vip_product_buyers') {
        const rows = await neonQuery('vip_product_buyers', { product: input.product, limit: input.limit || 50 });
        const fmtDate = d => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
        const buyers = rows.map(r => ({
          name: [r.first_name, r.last_name].filter(Boolean).join(' '),
          email: r.email,
          orderCount: Number(r.order_count),
          lastOrderDate: fmtDate(r.last_order_date),
          lifetimeValue: `$${Number(r.lifetime_value || 0).toFixed(2)}`,
          active: r.active,
        }));
        return ok({ product: input.product, count: rows.length, buyers }, buyers);
      }
      if (name === 'get_vip_by_state') {
        const rows = await neonQuery('vip_by_state', {});
        const mapped = rows.map(r => ({ state: r.state, count: Number(r.vip_count) }));
        return ok(mapped, mapped);
      }
      if (name === 'run_sql') {
        const result = await neonRawSelect(input.sql);
        return ok({ rowCount: result.rowCount, rows: result.rows, sql: result.sql }, result.rows);
      }
      return ok({ error: 'Unknown tool' });
    } catch (e) {
      return ok({ error: e.message });
    }
  };

  const sendChat = async () => {
    const text = chatInput.trim();
    if (!text || chatLoading) return;
    setChatInput('');
    setChatLoading(true);

    const userDisplay = { role: 'user', text };
    setDisplayMessages(prev => [...prev, userDisplay]);

    let msgs = [...apiMessages, { role: 'user', content: text }];
    setApiMessages(msgs);

    try {
      let lastExportRows = null;
      while (true) {
        const res = await fetch(`${CHAT_BASE}/vip-chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ messages: msgs }),
        });
        const text = await res.text();
        let data;
        try { data = JSON.parse(text); }
        catch (e) {
          if (res.status >= 500 || /^<(!doctype|html)/i.test(text.trim().slice(0, 20))) {
            throw new Error(`Jimmy ran out of time (30-second limit). Your question was too large to answer in one shot — try asking for fewer rows (e.g. "top 20" instead of "top 50"), or break the question into smaller pieces.`);
          }
          throw new Error(`Unexpected response from Jimmy (HTTP ${res.status}).`);
        }
        if (data.error) throw new Error(data.detail?.error?.message || JSON.stringify(data.detail) || data.error);
        if (data.type === 'error' && data.error?.message) throw new Error(`Jimmy backend error: ${data.error.message}`);
        if (!Array.isArray(data.content)) {
          throw new Error(`Jimmy returned an unexpected response shape (no content array). ${JSON.stringify(data).slice(0, 200)}`);
        }

        if (data.stop_reason === 'tool_use') {
          // Append assistant turn
          msgs = [...msgs, { role: 'assistant', content: data.content }];
          // Execute all tool calls
          const toolResults = [];
          for (const block of data.content) {
            if (block.type === 'tool_use') {
              const { result, exportRows } = await executeTool(block.name, block.input);
              if (exportRows?.length) lastExportRows = exportRows;
              toolResults.push({ type: 'tool_result', tool_use_id: block.id, content: result });
            }
          }
          msgs = [...msgs, { role: 'user', content: toolResults }];
        } else {
          // Final text response
          msgs = [...msgs, { role: 'assistant', content: data.content }];
          setApiMessages(msgs);
          const reply = data.content.find(b => b.type === 'text')?.text ?? '';
          setDisplayMessages(prev => [...prev, { role: 'assistant', text: reply, exportRows: lastExportRows }]);
          break;
        }
      }
    } catch (e) {
      setDisplayMessages(prev => [...prev, { role: 'assistant', text: `Error: ${e.message}`, isError: true }]);
    } finally {
      setChatLoading(false);
    }
  };

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [displayMessages, chatLoading]);

  // Search
  const [searchData, setSearchData] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [page, setPage] = useState(1);
  const [fields, setFields] = useState({ firstName: '', lastName: '', email: '', status: '', renewal: '' });

  const setField = (k, v) => setFields(f => ({ ...f, [k]: v }));

  const [avgOrders, setAvgOrders] = useState(null);
  const [metricsTrend, setMetricsTrend] = useState([]);
  const [growthTrend, setGrowthTrend] = useState([]);
  const [growthSummary, setGrowthSummary] = useState(null);
  const [growthWindow, setGrowthWindow] = useState(GROWTH_DEFAULT);
  const [growthGrain, setGrowthGrain] = useState('month');
  const [orderBuckets, setOrderBuckets] = useState([]);
  const [exporting, setExporting] = useState(null);
  const [expandedBucket, setExpandedBucket] = useState(null); // '4+', '2-3', '1', '0'
  const [bucketRows, setBucketRows] = useState([]);
  const [bucketLoading, setBucketLoading] = useState(false);
  const [bucketPageSize, setBucketPageSize] = useState(10);
  const [bucketPage, setBucketPage] = useState(1);

  useEffect(() => {
    fetchVipStats()
      .then(setStats)
      .catch(e => setStatsError(e.message))
      .finally(() => setStatsLoading(false));
    neonQuery('vip_orders_per_member')
      .then(rows => setAvgOrders(rows[0] ?? null))
      .catch(e => console.error('Avg orders load error:', e));
    neonQuery('vip_order_buckets')
      .then(setOrderBuckets)
      .catch(e => console.error('Order buckets load error:', e));
    neonQuery('vip_metrics_trend')
      .then(rows => setMetricsTrend(rows.map(r => ({
        date: new Date(r.metric_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        avgOrders: Number(r.avg_orders_per_vip),
        activeVips: Number(r.active_vips),
        totalOrders: Number(r.total_vip_orders),
      }))))
      .catch(e => console.error('Metrics trend load error:', e));
  }, []);

  // Growth data reloads whenever Greg changes the window. Grain follows the window --
  // yearly buckets for all-time, monthly otherwise -- so the bars stay readable.
  useEffect(() => {
    const w = GROWTH_WINDOWS.find(x => x.key === growthWindow) || GROWTH_WINDOWS[1];
    const since = growthSince(growthWindow);
    setGrowthGrain(w.grain);
    neonQuery('vip_growth_trend', { grain: w.grain, since })
      .then(setGrowthTrend)
      .catch(e => console.error('Growth trend load error:', e));
    neonQuery('vip_growth_summary', { since })
      .then(rows => setGrowthSummary(rows[0] ?? null))
      .catch(e => console.error('Growth summary load error:', e));
  }, [growthWindow]);

  const loadSearch = (p = 1) => {
    setSearchLoading(true);
    setSearchError(null);
    setPage(p);
    const params = {
      PageNumber: p,
      PageSize: PAGE_SIZE,
      ...(fields.firstName ? { FirstName: fields.firstName, Filter_FirstName: true } : {}),
      ...(fields.lastName  ? { LastName: fields.lastName,   Filter_LastName: true }  : {}),
      ...(fields.email     ? { Email: fields.email,         Filter_Email: true }     : {}),
      ...(fields.status    ? { Status: fields.status,       Filter_Status: true }    : {}),
      ...(fields.renewal !== '' ? { Renewal: Number(fields.renewal) } : {}),
    };
    fetchVipMembers(params)
      .then(setSearchData)
      .catch(e => setSearchError(e.message))
      .finally(() => setSearchLoading(false));
  };

  // ─── Geo data aggregation ─────────────────────────────────────────────────
  const stateMap = {};
  if (geoData) {
    geoData.forEach(item => {
      const abbr = (item.state ?? '').trim().toUpperCase();
      if (abbr && abbr.length === 2) stateMap[abbr] = Number(item.vip_count || 0);
    });
  }
  const stateSorted = Object.entries(stateMap).sort((a, b) => b[1] - a[1]);
  const maxCount = Math.max(1, ...Object.values(stateMap));
  const totalMembers = Object.values(stateMap).reduce((s, n) => s + n, 0);

  // ─── Goal progress ────────────────────────────────────────────────────────
  const current = stats?.active ?? 0;
  const pct = Math.min(100, current / GOAL * 100);
  const toGo = Math.max(0, GOAL - current);

  // ─── Search results ───────────────────────────────────────────────────────
  const rows = Array.isArray(searchData) ? searchData : searchData?.vipMembers ?? searchData?.data ?? [];
  const total = searchData?.totalRows ?? searchData?.total ?? rows.length;

  const statusColor = (s) => {
    if (s === true || s === 'Active') return { bg: '#e6f9f0', color: '#1a8a4a' };
    if (s === false || s === 'Inactive') return { bg: '#f5f5f5', color: '#5f6368' };
    return { bg: '#fff8e6', color: '#b06a00' };
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>VIP Club</h2>
      </div>

      {/* ── GOAL PROGRESS ── */}
      {(() => {
        const avgVal = Number(avgOrders?.avg_orders ?? 0);
        const ordersPct = Math.min(100, avgVal / ORDERS_GOAL * 100);
        return (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
            <div className="card" style={{ padding: '14px 16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
                <div>
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>Active VIPs</span>
                  <span style={{ fontSize: 11, color: 'var(--gray-500)', marginLeft: 6 }}>goal: {GOAL.toLocaleString()} by {GOAL_DATE}</span>
                </div>
                <span style={{ fontSize: 20, fontWeight: 800, color: 'var(--orange)' }}>
                  {statsLoading ? '...' : current.toLocaleString()}
                  <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--gray-500)', marginLeft: 4 }}>/ {GOAL.toLocaleString()}</span>
                </span>
              </div>
              <div style={{ height: 14, background: 'var(--gray-100)', borderRadius: 7, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${pct}%`, background: 'var(--orange)', borderRadius: 7, transition: 'width 1s ease' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 5 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--orange)' }}>{statsLoading ? '' : `${pct.toFixed(1)}% of goal`}</span>
                <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>{statsLoading ? '' : `${toGo.toLocaleString()} more needed`}</span>
              </div>
            </div>
            <div className="card" style={{ padding: '14px 16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
                <div>
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>Avg Orders / VIP</span>
                  <span style={{ fontSize: 11, color: 'var(--gray-500)', marginLeft: 6 }}>goal: {ORDERS_GOAL} (last 365 days)</span>
                </div>
                <span style={{ fontSize: 20, fontWeight: 800, color: avgVal >= ORDERS_GOAL ? '#1a8a4a' : 'var(--orange)' }}>
                  {avgOrders ? avgVal.toFixed(2) : '...'}
                  <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--gray-500)', marginLeft: 4 }}>/ {ORDERS_GOAL}</span>
                </span>
              </div>
              <div style={{ height: 14, background: 'var(--gray-100)', borderRadius: 7, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${ordersPct}%`, background: avgVal >= ORDERS_GOAL ? '#1a8a4a' : 'var(--orange)', borderRadius: 7, transition: 'width 1s ease' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 5 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: avgVal >= ORDERS_GOAL ? '#1a8a4a' : 'var(--orange)' }}>{avgOrders ? `${ordersPct.toFixed(1)}% of goal` : ''}</span>
                <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>{avgOrders ? `${avgOrders.total_orders?.toLocaleString()} orders from ${avgOrders.active_vips?.toLocaleString()} VIPs` : ''}</span>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── NET VIP GROWTH (Greg: sign 'em up AND keep 'em on) ── */}
      {growthTrend.length > 0 && (() => {
        const data = growthTrend.map(r => {
          const gains = Number(r.joined) + Number(r.rejoined);
          const losses = Number(r.lapsed);
          const d = new Date(r.period);
          return {
            label: growthGrain === 'year'
              ? String(d.getUTCFullYear())
              : d.toLocaleDateString('en-US', { month: 'short', year: '2-digit', timeZone: 'UTC' }),
            joined: Number(r.joined),
            rejoined: Number(r.rejoined),
            // Losses plot downward, so the bar reads as a loss at a glance.
            lapsed: -losses,
            net: gains - losses,
            wonBack: Number(r.auto_renew_on),
          };
        });
        const s = growthSummary || {};
        const net = Number(s.net || 0);
        const lapsed = Number(s.lapsed || 0);
        const optedOut = Number(s.lapsed_opted_out || 0);
        const optedOutPct = lapsed > 0 ? Math.round((optedOut / lapsed) * 100) : 0;

        return (
          <div className="card" style={{ marginBottom: 12, padding: '14px 16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
              <div>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>Net VIP Growth</span>
                <span style={{ fontSize: 11, color: 'var(--gray-500)', marginLeft: 6 }}>
                  signups and win-backs vs members lost
                </span>
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                {GROWTH_WINDOWS.map(w => (
                  <button
                    key={w.key}
                    onClick={() => setGrowthWindow(w.key)}
                    style={{
                      fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 5, cursor: 'pointer',
                      border: '1px solid ' + (growthWindow === w.key ? 'var(--orange)' : 'var(--gray-200)'),
                      background: growthWindow === w.key ? 'var(--orange)' : '#fff',
                      color: growthWindow === w.key ? '#fff' : 'var(--gray-500)',
                    }}
                  >{w.label}</button>
                ))}
              </div>
            </div>

            {/* Headline, in Greg's own framing: gains, saves, losses, net */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 12 }}>
              {[
                { label: 'Signups', value: Number(s.joined || 0), color: '#1a8a4a', sign: '+' },
                { label: 'Re-joined', value: Number(s.rejoined || 0), color: '#1a8a4a', sign: '+' },
                { label: 'Lapsed', value: lapsed, color: '#c0392b', sign: '−' },
                { label: 'Net change', value: net, color: net >= 0 ? '#1a8a4a' : '#c0392b', sign: net >= 0 ? '+' : '−' },
              ].map(({ label, value, color, sign }) => (
                <div key={label} style={{ background: 'var(--gray-50, #fafafa)', borderRadius: 6, padding: '8px 10px' }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
                  <div style={{ fontSize: 19, fontWeight: 800, color }}>{sign}{Math.abs(value).toLocaleString()}</div>
                </div>
              ))}
            </div>

            <ResponsiveContainer width="100%" height={230}>
              <ComposedChart data={data} margin={{ top: 5, right: 16, bottom: 5, left: 4 }} stackOffset="sign">
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#888' }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 10, fill: '#888' }} />
                <Tooltip
                  formatter={(v, name) => [Math.abs(v).toLocaleString(), name]}
                  labelStyle={{ fontWeight: 700 }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <ReferenceLine y={0} stroke="#999" />
                <Bar dataKey="joined"   name="Signups"   stackId="a" fill="#1a8a4a" />
                <Bar dataKey="rejoined" name="Re-joined" stackId="a" fill="#7bc47f" />
                <Bar dataKey="lapsed"   name="Lapsed"    stackId="a" fill="#c0392b" />
                <Line type="monotone" dataKey="net" name="Net" stroke="var(--navy)" strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>

            {/* The "keep 'em on" half: of everyone who left, how many chose to? */}
            {lapsed > 0 && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--gray-100)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 5 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy)' }}>Why we lost them</span>
                  <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>
                    {optedOut.toLocaleString()} chose not to renew &middot; {(lapsed - optedOut).toLocaleString()} lost another way
                  </span>
                </div>
                <div style={{ display: 'flex', height: 12, borderRadius: 6, overflow: 'hidden', background: 'var(--gray-100)' }}>
                  <div style={{ width: optedOutPct + '%', background: '#e67e22' }} title={optedOutPct + '% opted out'} />
                  <div style={{ width: (100 - optedOutPct) + '%', background: '#95a5a6' }} title={(100 - optedOutPct) + '% card/other'} />
                </div>
                <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 5 }}>
                  {OPTOUT_TRACKING_NOTE}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── TREND CHART ── */}
      {metricsTrend.length > 0 && (
        <div className="card" style={{ marginBottom: 12, padding: '14px 16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>Avg Orders / VIP Trend</span>
            <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>Daily tracking — goal: {ORDERS_GOAL}</span>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={metricsTrend} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#888' }} />
              <YAxis domain={[0, Math.max(ORDERS_GOAL + 0.5, Math.max(...metricsTrend.map(d => d.avgOrders)) + 0.3)]} tick={{ fontSize: 10, fill: '#888' }} tickFormatter={v => v.toFixed(1)} />
              <Tooltip formatter={(v, name) => [v.toFixed(2), name === 'avgOrders' ? 'Avg Orders/VIP' : name]} labelStyle={{ fontWeight: 700 }} />
              <ReferenceLine y={ORDERS_GOAL} stroke="#1a8a4a" strokeDasharray="6 3" label={{ value: `Goal: ${ORDERS_GOAL}`, position: 'right', fontSize: 10, fill: '#1a8a4a' }} />
              <Line type="monotone" dataKey="avgOrders" stroke="var(--orange)" strokeWidth={2} dot={{ r: 3, fill: 'var(--orange)' }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ── STAT CARDS ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 8 }}>
        {[
          { label: 'Total Active', value: stats?.active, color: 'var(--orange)' },
          { label: 'Auto-Renewing', value: stats?.renewing, color: '#1a8a4a' },
        ].map(({ label, value, color }) => (
          <div key={label} className="card" style={{ padding: '10px 14px' }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color }}>
              {statsLoading ? '—' : (value ?? 0).toLocaleString()}
            </div>
          </div>
        ))}

        {/* Expired (Winback) card — includes Dotdigital sync */}
        <div className="card" style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Expired (Winback)</div>
            {showSync && (
              <button
                onClick={handleSyncExpired}
                disabled={syncingExpired}
                className="btn btn-primary"
                style={{ fontSize: 9, padding: '2px 8px', whiteSpace: 'nowrap', opacity: syncingExpired ? 0.7 : 1, marginTop: -2 }}
              >
                {syncingExpired ? 'Syncing…' : 'Sync to Dotdigital'}
              </button>
            )}
          </div>
          <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--red)' }}>
            {statsLoading ? '—' : (stats?.expired ?? 0).toLocaleString()}
          </div>
          <div style={{ fontSize: 9, color: syncResultExpired?.error ? 'var(--red)' : 'var(--gray-400)', marginTop: 4, lineHeight: 1.3 }}>
            {syncingExpired && syncProgressExpired && `Syncing… ${syncProgressExpired.cursor}${syncProgressExpired.totalVips ? '/' + syncProgressExpired.totalVips : ''} · ${syncProgressExpired.added} added`}
            {!syncingExpired && syncResultExpired && !syncResultExpired.error && (
              <>Synced {new Date(syncResultExpired.syncedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} · {syncResultExpired.added} added · {syncResultExpired.suppressed} suppressed</>
            )}
            {!syncingExpired && syncResultExpired?.error && `Sync failed: ${syncResultExpired.error}`}
            {!syncingExpired && !syncResultExpired && 'Not yet synced · runs nightly 2am ET'}
          </div>
        </div>

        {/* Not Renewing card — includes Dotdigital sync */}
        <div className="card" style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Not Renewing</div>
            {showSync && (
              <button
                onClick={handleSync}
                disabled={syncing}
                className="btn btn-primary"
                style={{ fontSize: 9, padding: '2px 8px', whiteSpace: 'nowrap', opacity: syncing ? 0.7 : 1, marginTop: -2 }}
              >
                {syncing ? 'Syncing…' : 'Sync to Dotdigital'}
              </button>
            )}
          </div>
          <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--gray-500)' }}>
            {statsLoading ? '—' : (stats?.notRenewing ?? 0).toLocaleString()}
          </div>
          <div style={{ fontSize: 9, color: syncResult?.error ? 'var(--red)' : 'var(--gray-400)', marginTop: 4, lineHeight: 1.3 }}>
            {syncing && 'Syncing…'}
            {!syncing && syncResult && !syncResult.error && (
              <>Synced {new Date(syncResult.syncedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} · {syncResult.totalVips} contacts</>
            )}
            {!syncing && syncResult?.error && `Sync failed: ${syncResult.error}`}
            {!syncing && !syncResult && 'Not yet synced · runs nightly 2am ET'}
          </div>
        </div>
      </div>

      {/* ── ACTIVITY CARDS ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 12 }}>
        {[
          { label: 'New This Week', value: stats?.week?.newVips },
          { label: 'Reactivated This Week', value: stats?.week?.reactivated },
          { label: 'New This Month', value: stats?.month?.newVips },
          { label: 'Reactivated This Month', value: stats?.month?.reactivated },
        ].map(({ label, value }) => (
          <div key={label} className="card" style={{ padding: '10px 14px' }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--navy)' }}>
              {statsLoading ? '—' : (value ?? 0).toLocaleString()}
            </div>
          </div>
        ))}
      </div>

      {/* ── ORDER FREQUENCY BUCKETS ── */}
      {orderBuckets.length > 0 && (() => {
        const bucketMeta = {
          '4+': { label: '4+ Orders', color: '#1a8a4a', icon: '🔥' },
          '2-3': { label: '2–3 Orders', color: '#2563eb', icon: '📈' },
          '1': { label: '1 Order', color: 'var(--orange)', icon: '👋' },
          '0': { label: '0 Orders', color: '#dc3545', icon: '😴' },
        };
        const exportBucket = async (bucket, e) => {
          e.stopPropagation();
          setExporting(bucket);
          try {
            const rows = await neonQuery('vip_bucket_export', { bucket });
            const header = 'First Name,Last Name,Email,Orders (365d)\n';
            const csv = header + rows.map(r =>
              `"${(r.firstname || '').replace(/"/g, '""')}","${(r.lastname || '').replace(/"/g, '""')}","${(r.email || '').replace(/"/g, '""')}",${r.order_count}`
            ).join('\n');
            const blob = new Blob([csv], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = `vip-${bucket.replace('+', 'plus')}-orders.csv`; a.click();
            URL.revokeObjectURL(url);
          } catch (e2) { console.error('Export error:', e2); }
          setExporting(null);
        };
        const clickBucket = async (key) => {
          if (expandedBucket === key) { setExpandedBucket(null); return; }
          setExpandedBucket(key);
          setBucketPageSize(10);
          setBucketPage(1);
          if (bucketRows.length === 0 || expandedBucket !== key) {
            setBucketLoading(true);
            try {
              const rows = await neonQuery('vip_bucket_export', { bucket: key });
              setBucketRows(rows);
            } catch (e) { console.error('Bucket detail error:', e); setBucketRows([]); }
            setBucketLoading(false);
          }
        };
        const totalVips = orderBuckets.reduce((s, b) => s + Number(b.vip_count), 0);
        return (
          <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: expandedBucket ? 0 : 12 }}>
            {['4+', '2-3', '1', '0'].map(key => {
              const b = orderBuckets.find(r => r.bucket === key) || { vip_count: 0, total_orders: 0, avg_order_value: '0' };
              const meta = bucketMeta[key];
              const pctOfVips = totalVips > 0 ? (Number(b.vip_count) / totalVips * 100).toFixed(1) : '0';
              const isExpanded = expandedBucket === key;
              return (
                <div key={key} className="card" onClick={() => clickBucket(key)}
                  style={{ padding: '14px 16px', cursor: 'pointer', outline: isExpanded ? `2px solid ${meta.color}` : 'none', transition: 'outline 0.15s' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: meta.color }}>{meta.icon} {meta.label}</span>
                    <button onClick={(e) => exportBucket(key, e)} disabled={exporting === key}
                      style={{ fontSize: 9, padding: '3px 8px', background: '#f0f2f5', border: '1px solid #ddd', borderRadius: 4, cursor: 'pointer', color: '#555', fontWeight: 600 }}>
                      {exporting === key ? '...' : '↓ CSV'}
                    </button>
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: meta.color, marginBottom: 2 }}>{Number(b.vip_count).toLocaleString()}</div>
                  <div style={{ fontSize: 10, color: 'var(--gray-500)', marginBottom: 8 }}>{pctOfVips}% of active VIPs</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #eee', paddingTop: 8 }}>
                    <div>
                      <div style={{ fontSize: 9, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.3 }}>Orders</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy)' }}>{Number(b.total_orders).toLocaleString()}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 9, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.3 }}>Avg Value</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy)' }}>${Number(b.avg_order_value).toFixed(2)}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {expandedBucket && (
            <div className="card" style={{ marginTop: 8, marginBottom: 12, padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '10px 16px', background: '#f8f9fa', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy)' }}>
                  {bucketMeta[expandedBucket].icon} {bucketMeta[expandedBucket].label} — {bucketLoading ? '...' : `${bucketRows.length.toLocaleString()} VIPs`}
                </span>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span style={{ fontSize: 10, color: 'var(--gray-500)' }}>Show:</span>
                  {[10, 25, 50, 100].map(n => (
                    <button key={n} onClick={() => { setBucketPageSize(n); setBucketPage(1); }}
                      style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, border: 'none', cursor: 'pointer', fontWeight: 600, background: bucketPageSize === n ? 'var(--navy)' : '#e5e7eb', color: bucketPageSize === n ? '#fff' : '#555' }}>
                      {n}
                    </button>
                  ))}
                </div>
              </div>
              {bucketLoading ? (
                <div style={{ padding: 20, textAlign: 'center', color: '#999', fontSize: 12 }}>Loading...</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#f8f9fa' }}>
                      <th style={{ padding: '8px 16px', textAlign: 'left', fontWeight: 700, color: 'var(--gray-500)', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.3 }}>First Name</th>
                      <th style={{ padding: '8px 16px', textAlign: 'left', fontWeight: 700, color: 'var(--gray-500)', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.3 }}>Last Name</th>
                      <th style={{ padding: '8px 16px', textAlign: 'left', fontWeight: 700, color: 'var(--gray-500)', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.3 }}>Email</th>
                      <th style={{ padding: '8px 16px', textAlign: 'center', fontWeight: 700, color: 'var(--gray-500)', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.3 }}>Orders (365d)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bucketRows.slice((bucketPage - 1) * bucketPageSize, bucketPage * bucketPageSize).map((r, i) => (
                      <tr key={i} style={{ borderTop: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '7px 16px' }}>{r.firstname || '—'}</td>
                        <td style={{ padding: '7px 16px' }}>{r.lastname || '—'}</td>
                        <td style={{ padding: '7px 16px', color: '#2563eb' }}>{r.email || '—'}</td>
                        <td style={{ padding: '7px 16px', textAlign: 'center', fontWeight: 700 }}>{r.order_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {!bucketLoading && bucketRows.length > 0 && (() => {
                const totalPages = Math.ceil(bucketRows.length / bucketPageSize);
                const startRow = (bucketPage - 1) * bucketPageSize + 1;
                const endRow = Math.min(bucketPage * bucketPageSize, bucketRows.length);
                return (
                  <div style={{ padding: '8px 16px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>
                      {startRow}–{endRow} of {bucketRows.length.toLocaleString()}
                    </span>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      <button onClick={() => setBucketPage(1)} disabled={bucketPage === 1}
                        style={{ fontSize: 10, padding: '3px 8px', border: '1px solid #ddd', borderRadius: 4, background: '#fff', cursor: bucketPage === 1 ? 'default' : 'pointer', opacity: bucketPage === 1 ? 0.4 : 1 }}>First</button>
                      <button onClick={() => setBucketPage(p => p - 1)} disabled={bucketPage === 1}
                        style={{ fontSize: 10, padding: '3px 8px', border: '1px solid #ddd', borderRadius: 4, background: '#fff', cursor: bucketPage === 1 ? 'default' : 'pointer', opacity: bucketPage === 1 ? 0.4 : 1 }}>Prev</button>
                      <span style={{ fontSize: 11, color: 'var(--navy)', fontWeight: 600, padding: '0 6px' }}>
                        {bucketPage} / {totalPages}
                      </span>
                      <button onClick={() => setBucketPage(p => p + 1)} disabled={bucketPage === totalPages}
                        style={{ fontSize: 10, padding: '3px 8px', border: '1px solid #ddd', borderRadius: 4, background: '#fff', cursor: bucketPage === totalPages ? 'default' : 'pointer', opacity: bucketPage === totalPages ? 0.4 : 1 }}>Next</button>
                      <button onClick={() => setBucketPage(totalPages)} disabled={bucketPage === totalPages}
                        style={{ fontSize: 10, padding: '3px 8px', border: '1px solid #ddd', borderRadius: 4, background: '#fff', cursor: bucketPage === totalPages ? 'default' : 'pointer', opacity: bucketPage === totalPages ? 0.4 : 1 }}>Last</button>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
          </>
        );
      })()}

      {/* ── GEOGRAPHIC DISTRIBUTION ── */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => { setGeoOpen(o => !o); if (!geoData && !geoLoading) { setGeoLoading(true); fetchVipGeoData().then(setGeoData).finally(() => setGeoLoading(false)); } }}>
          <span>Active VIP Distribution by State {stateSorted.length > 0 && `(${stateSorted.length} states)`}</span>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {geoOpen && ['table', 'map'].map(v => (
              <button key={v} onClick={e => { e.stopPropagation(); setGeoView(v); }} style={{ fontSize: 10, padding: '2px 10px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 700, background: geoView === v ? 'var(--orange)' : 'var(--gray-200)', color: geoView === v ? '#fff' : 'var(--gray-600)' }}>
                {v === 'table' ? '☰ States' : '🗺 Map'}
              </button>
            ))}
            {geoOpen && geoView === 'map' && (
              <>
                <button onClick={e => { e.stopPropagation(); setShowDots(d => !d); }} style={{ fontSize: 10, padding: '2px 10px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 700, background: showDots ? 'var(--navy)' : 'var(--gray-200)', color: showDots ? '#fff' : 'var(--gray-600)' }}>
                  ⬤ Dots
                </button>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }} onClick={e => e.stopPropagation()}>
                  <button onClick={() => setMapScale(s => Math.max(40, s - 10))} style={{ fontSize: 12, width: 22, height: 22, borderRadius: 6, border: 'none', cursor: 'pointer', fontWeight: 700, background: 'var(--gray-200)', color: 'var(--gray-600)', lineHeight: 1 }}>−</button>
                  <span style={{ fontSize: 10, color: 'var(--gray-500)', minWidth: 32, textAlign: 'center' }}>{mapScale}%</span>
                  <button onClick={() => setMapScale(s => Math.min(150, s + 10))} style={{ fontSize: 12, width: 22, height: 22, borderRadius: 6, border: 'none', cursor: 'pointer', fontWeight: 700, background: 'var(--gray-200)', color: 'var(--gray-600)', lineHeight: 1 }}>+</button>
                </div>
              </>
            )}
            <span style={{ fontSize: 12, color: 'var(--gray-500)', marginLeft: 4 }}>{geoOpen ? '▲' : '▼'}</span>
          </div>
        </div>

        {geoOpen && geoLoading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading map data...</div>}
        {geoOpen && !geoLoading && stateSorted.length === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No geographic data available.</div>
        )}

        {/* ── TABLE VIEW ── */}
        {geoOpen && geoView === 'table' && stateSorted.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 4px 8px 12px', fontWeight: 700, color: 'var(--gray-500)', width: 28 }}></th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>State</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Members</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>% Share</th>
                  <th style={{ padding: '8px 12px' }}></th>
                </tr>
              </thead>
              <tbody>
                {stateSorted.map(([abbr, count], i) => {
                  const isExpanded = expandedStates.has(abbr);
                  const isLoadingCities = cityLoading.has(abbr);
                  const cities = cityData[abbr] ?? [];
                  const rowBg = i % 2 === 0 ? '#fff' : 'var(--gray-50)';
                  return (
                    <React.Fragment key={abbr}>
                      <tr style={{ borderBottom: isExpanded ? 'none' : '1px solid var(--gray-100)', background: rowBg }}>
                        <td style={{ padding: '6px 4px 6px 10px' }}>
                          <button onClick={() => toggleStateExpand(abbr)} style={{ width: 20, height: 20, borderRadius: 4, border: '1px solid var(--gray-300)', background: isExpanded ? 'var(--orange)' : '#fff', color: isExpanded ? '#fff' : 'var(--gray-500)', fontSize: 14, lineHeight: 1, cursor: 'pointer', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
                            {isLoadingCities ? '…' : isExpanded ? '−' : '+'}
                          </button>
                        </td>
                        <td style={{ padding: '8px 12px', color: 'var(--navy)', fontWeight: 700, fontSize: 13 }}>{abbr}</td>
                        <td style={{ padding: '8px 8px', textAlign: 'right', color: 'var(--orange)', fontWeight: 700 }}>{count.toLocaleString()}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', color: 'var(--gray-500)' }}>
                          {totalMembers > 0 ? `${(count / totalMembers * 100).toFixed(1)}%` : '—'}
                        </td>
                        <td style={{ padding: '8px 16px 8px 8px' }}>
                          <div style={{ height: 6, background: 'var(--gray-100)', borderRadius: 3, overflow: 'hidden', width: 80 }}>
                            <div style={{ height: '100%', width: `${count / maxCount * 100}%`, background: 'var(--orange)', borderRadius: 3 }} />
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr style={{ borderBottom: '1px solid var(--gray-100)', background: rowBg }}>
                          <td colSpan={5} style={{ padding: '0 12px 10px 40px' }}>
                            {isLoadingCities ? (
                              <div style={{ fontSize: 11, color: 'var(--gray-400)', padding: '6px 0' }}>Loading cities…</div>
                            ) : cities.length === 0 ? (
                              <div style={{ fontSize: 11, color: 'var(--gray-400)', padding: '6px 0' }}>No city data available.</div>
                            ) : (
                              <div>
                                <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                                  {['count', 'alpha'].map(mode => (
                                    <button key={mode} onClick={() => setCitySort(mode)} style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 700, background: citySort === mode ? 'var(--navy)' : 'var(--gray-200)', color: citySort === mode ? '#fff' : 'var(--gray-600)' }}>
                                      {mode === 'count' ? '# Members' : 'A–Z'}
                                    </button>
                                  ))}
                                </div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', paddingTop: 0 }}>
                                {[...cities].sort(citySort === 'alpha' ? (a, b) => a.city.localeCompare(b.city) : () => 0).map(c => (
                                  <span key={c.city}
                                    style={{ fontSize: 11, color: 'var(--gray-600)', whiteSpace: 'nowrap', cursor: 'pointer', position: 'relative' }}
                                    onClick={async e => {
                                      const key = `${abbr}:${c.city}`;
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      // Toggle: click again to close
                                      if (cityMembersTip?.city === c.city && cityMembersTip?.state === abbr) {
                                        setCityMembersTip(null);
                                        return;
                                      }
                                      setCityMembersTip({ state: abbr, city: c.city, x: rect.left, y: rect.bottom + 4, members: cityMembersCache[key] ?? null, loading: !cityMembersCache[key] });
                                      if (!cityMembersCache[key]) {
                                        const members = await neonQuery('vip_members_by_city', { state: abbr, city: c.city }).catch(() => []);
                                        setCityMembersCache(prev => ({ ...prev, [key]: members }));
                                        setCityMembersTip(t => t?.city === c.city && t?.state === abbr ? { ...t, members, loading: false } : t);
                                      }
                                    }}>
                                    <span style={{ color: 'var(--navy)', fontWeight: 600, borderBottom: '1px dotted var(--gray-400)' }}>{toTitleCase(c.city)}</span>
                                    <span style={{ color: 'var(--orange)', marginLeft: 4 }}>{Number(c.vip_count).toLocaleString()}</span>
                                  </span>
                                ))}
                              </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* ── MAP VIEW ── */}
        {geoOpen && geoView === 'map' && stateSorted.length > 0 && (
          <div style={{ padding: '12px 16px', position: 'relative', userSelect: 'none' }}>
            <ComposableMap
              projection="geoAlbersUsa"
              style={{ width: `${mapScale}%`, height: 'auto', display: 'block', margin: '0 auto' }}
            >
              <Geographies geography={GEO_URL}>
                {({ geographies }) =>
                  geographies.map(geo => {
                    const name = geo.properties?.name;
                    const abbr = NAME_TO_ABBR[name];
                    const count = abbr ? (stateMap[abbr] ?? 0) : 0;
                    const intensity = count / maxCount;
                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        style={{
                          default: { fill: heatColor(intensity), stroke: '#fff', strokeWidth: 0.5, outline: 'none' },
                          hover:   { fill: heatColor(intensity), stroke: '#fff', strokeWidth: 0.5, outline: 'none', opacity: 0.8 },
                          pressed: { fill: heatColor(intensity), stroke: '#fff', strokeWidth: 0.5, outline: 'none' },
                        }}
                        onMouseEnter={e => setTooltip({ x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY, abbr: abbr ?? name, count })}
                        onMouseLeave={() => setTooltip(null)}
                      />
                    );
                  })
                }
              </Geographies>

              {showDots && Object.entries(stateMap).map(([abbr, count]) => {
                const coords = STATE_CENTROIDS[abbr];
                if (!coords || !count) return null;
                const r = MAX_DOT_R * Math.sqrt(count / maxCount);
                return (
                  <Marker key={abbr} coordinates={coords}>
                    <circle
                      r={r}
                      fill="var(--navy)"
                      fillOpacity={0.65}
                      stroke="#fff"
                      strokeWidth={1}
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={e => setTooltip({ x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY, abbr, count })}
                      onMouseLeave={() => setTooltip(null)}
                    />
                    {r >= 10 && (
                      <text
                        textAnchor="middle"
                        dominantBaseline="middle"
                        style={{ fontSize: Math.max(7, Math.min(11, r * 0.6)), fontWeight: 700, fill: '#fff', pointerEvents: 'none', fontFamily: 'Montserrat, sans-serif' }}
                      >
                        {count >= 1000 ? `${(count / 1000).toFixed(1)}k` : count}
                      </text>
                    )}
                  </Marker>
                );
              })}
            </ComposableMap>

            {tooltip && (
              <div style={{ position: 'absolute', left: tooltip.x + 12, top: tooltip.y - 10, background: 'var(--navy)', color: '#fff', fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 6, pointerEvents: 'none', whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
                {tooltip.abbr}: {tooltip.count.toLocaleString()} members
                {totalMembers > 0 && <span style={{ fontWeight: 400, opacity: 0.8 }}> ({(tooltip.count / totalMembers * 100).toFixed(1)}%)</span>}
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, justifyContent: 'flex-end' }}>
              <span style={{ fontSize: 10, color: 'var(--gray-500)' }}>0</span>
              <div style={{ width: 120, height: 10, borderRadius: 4, background: 'linear-gradient(to right, #f5e8d8, #DC582A)' }} />
              <span style={{ fontSize: 10, color: 'var(--gray-500)' }}>{maxCount.toLocaleString()}</span>
            </div>
          </div>
        )}
      </div>

      {/* ── BEST VIP CUSTOMERS ── */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={handleBestToggle}>
          <span>Best VIP Customers <span style={{ fontSize: 10, fontWeight: 400, color: 'var(--gray-500)' }}>ranked by orders/year</span></span>
          <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>{bestOpen ? '▲' : '▼'}</span>
        </div>
        {bestOpen && bestLoading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
        {bestOpen && bestError && <div style={{ padding: 16, fontSize: 12, color: 'var(--red)' }}>Error: {bestError}</div>}
        {bestOpen && bestData && bestData.length === 0 && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No data available.</div>}
        {bestOpen && bestData && bestData.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                  <th style={{ textAlign: 'center', padding: '8px 10px', fontWeight: 700, color: 'var(--gray-500)', width: 36 }}>#</th>
                  <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Member</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Joined</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Years</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Total Orders</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>AOV</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Orders/Year</th>
                </tr>
              </thead>
              <tbody>
                {bestData.map((row, i) => {
                  const medalBg = i === 0 ? '#fdf3d0' : i === 1 ? '#f5f5f5' : i === 2 ? '#fef0e8' : (i % 2 === 0 ? '#fff' : 'var(--gray-50)');
                  const joined = row.joinedDate ? new Date(row.joinedDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '—';
                  return (
                    <tr key={i} onClick={() => navigateTo?.('customer-search', null, { email: row.customerEmail ?? '' })} style={{ borderBottom: '1px solid var(--gray-100)', background: medalBg, cursor: 'pointer' }}>
                      <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 700, color: i === 0 ? '#b8860b' : i === 1 ? '#888' : i === 2 ? '#b5651d' : 'var(--gray-500)', fontSize: 13 }}>
                        {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : i + 1}
                      </td>
                      <td style={{ padding: '8px 8px', color: 'var(--navy)', fontWeight: 600 }}>
                        {toTitleCase(row.customerName)}
                        <div style={{ fontSize: 10, color: 'var(--gray-500)', fontWeight: 400 }}>{row.customerEmail}</div>
                      </td>
                      <td style={{ padding: '8px 8px', textAlign: 'right', color: 'var(--gray-700)' }}>{joined}</td>
                      <td style={{ padding: '8px 8px', textAlign: 'right', color: 'var(--gray-500)' }}>{Number(row.yearsSinceJoining).toFixed(1)}</td>
                      <td style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 700, color: 'var(--navy)' }}>{row.totalOrders.toLocaleString()}</td>
                      <td style={{ padding: '8px 8px', textAlign: 'right', color: 'var(--gray-700)' }}>${Number(row.avgOrderAmount).toFixed(2)}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 800, color: 'var(--orange)', fontSize: 13 }}>
                        {Number(row.ordersPerYear).toFixed(1)}<span style={{ fontSize: 10, fontWeight: 400, color: 'var(--gray-500)' }}>/yr</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── SEARCH ── */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-header">Search Members</div>
        <div style={{ padding: '12px 16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 10 }}>
            {[{ key: 'firstName', label: 'First Name' }, { key: 'lastName', label: 'Last Name' }, { key: 'email', label: 'Email' }].map(({ key, label }) => (
              <div key={key}>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>{label.toUpperCase()}</div>
                <input type="text" value={fields[key]} onChange={e => setField(key, e.target.value)} onKeyDown={e => e.key === 'Enter' && loadSearch(1)} placeholder={label} className="form-input" style={{ fontSize: 11, width: '100%' }} />
              </div>
            ))}
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>STATUS</div>
              <select value={fields.status} onChange={e => setField('status', e.target.value)} className="form-select" style={{ fontSize: 11, width: '100%' }}>
                <option value="">All</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>RENEWAL</div>
              <select value={fields.renewal} onChange={e => setField('renewal', e.target.value)} className="form-select" style={{ fontSize: 11, width: '100%' }}>
                <option value="">All</option>
                <option value="1">Auto-Renew</option>
                <option value="0">No Renew</option>
              </select>
            </div>
          </div>
          <button onClick={() => loadSearch(1)} className="btn btn-primary" style={{ fontSize: 11 }}>Search</button>
          <button onClick={() => { setFields({ firstName: '', lastName: '', email: '', status: '', renewal: '' }); setSearchData(null); }} className="btn btn-navy" style={{ fontSize: 11, marginLeft: 8 }}>Clear</button>
        </div>
      </div>

      {/* ── SEARCH RESULTS ── */}
      {searchData !== null && (
        <div className="card">
          <div className="card-header">Results {total > 0 && `(${total.toLocaleString()})`}</div>
          {searchLoading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
          {searchError && <div style={{ padding: 16, fontSize: 12, color: 'var(--red)' }}>Error: {searchError}</div>}
          {!searchLoading && !searchError && rows.length === 0 && (
            <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No VIP members found.</div>
          )}
          {rows.length > 0 && (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                      <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Member</th>
                      <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Email</th>
                      <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Joined</th>
                      <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Expires</th>
                      <th style={{ textAlign: 'center', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Status</th>
                      <th style={{ textAlign: 'center', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Auto-Renew</th>
                      <th style={{ textAlign: 'center', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Payment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => {
                      const sc = statusColor(row.status ?? row.vipStatus);
                      const hasPayment = Boolean(row.customerPaymentProfileId);
                      return (
                        <tr key={i} onClick={() => navigateTo?.('customer-search', null, { email: row.customerEmail ?? row.email ?? '' })} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)', cursor: 'pointer' }}>
                          <td style={{ padding: '8px 12px', color: 'var(--navy)', fontWeight: 600 }}>
                            {toTitleCase(row.customerName ?? [row.firstName, row.lastName].filter(Boolean).join(' ') ?? '—')}
                            <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>ID: {row.customerId ?? row.id ?? '—'}</div>
                          </td>
                          <td style={{ padding: '8px 8px', color: 'var(--gray-500)', fontSize: 11 }}>{row.customerEmail ?? row.email ?? '—'}</td>
                          <td style={{ padding: '8px 8px', color: 'var(--gray-700)' }}>{row.joinedDate ?? '—'}</td>
                          <td style={{ padding: '8px 8px', color: 'var(--gray-700)' }}>{row.expirationDate ?? '—'}</td>
                          <td style={{ padding: '8px 8px', textAlign: 'center' }}>
                            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10, ...sc }}>
                              {row.status === true ? 'Active' : row.status === false ? 'Inactive' : row.status ?? '—'}
                            </span>
                          </td>
                          <td style={{ padding: '8px 8px', textAlign: 'center' }}>
                            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10, background: !row.doNotRenewal ? '#e6f9f0' : '#f5f5f5', color: !row.doNotRenewal ? '#1a8a4a' : '#5f6368' }}>
                              {row.doNotRenewal ? 'No' : 'Yes'}
                            </span>
                          </td>
                          <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10, background: hasPayment ? '#e6f9f0' : '#fef3f0', color: hasPayment ? '#1a8a4a' : '#c0392b' }}>
                              {hasPayment ? '✓' : 'Missing'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {total > PAGE_SIZE && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: 12 }}>
                  <button onClick={() => loadSearch(Math.max(1, page - 1))} disabled={page === 1} className="btn btn-navy" style={{ fontSize: 11 }}>← Prev</button>
                  <span style={{ fontSize: 11, alignSelf: 'center' }}>Page {page}</span>
                  <button onClick={() => loadSearch(page + 1)} disabled={rows.length < PAGE_SIZE} className="btn btn-navy" style={{ fontSize: 11 }}>Next →</button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── ASK CLAUDE ── */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => setChatOpen(o => !o)}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <img src={jimmyImg} alt="Jimmy" style={{ width: 48, height: 48, objectFit: 'contain' }} />
            Ask Jimmy about VIP data
          </span>
          <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>{chatOpen ? '▲' : '▼'}</span>
        </div>

        {chatOpen && (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {/* Message history */}
            <div style={{ maxHeight: 360, overflowY: 'auto', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {displayMessages.length === 0 && (
                <div style={{ fontSize: 12, color: 'var(--gray-400)', textAlign: 'center', padding: '16px 0' }}>
                  Ask anything about your VIP members — stats, top customers, member lookups, and more.
                </div>
              )}
              {displayMessages.map((msg, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start', alignItems: 'flex-start', gap: 8 }}>
                  {msg.role === 'assistant' && (
                    <img src={jimmyImg} alt="Jimmy" style={{ width: 28, height: 28, objectFit: 'contain', flexShrink: 0, marginTop: 2 }} />
                  )}
                  <div style={{ maxWidth: '80%', display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{
                      padding: '8px 12px',
                      borderRadius: msg.role === 'user' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                      background: msg.role === 'user' ? 'var(--orange)' : (msg.isError ? '#fef3f0' : 'var(--gray-50)'),
                      color: msg.role === 'user' ? '#fff' : (msg.isError ? 'var(--red)' : 'var(--navy)'),
                      fontSize: 12,
                      lineHeight: 1.5,
                      border: msg.role === 'assistant' ? '1px solid var(--gray-100)' : 'none',
                      whiteSpace: 'pre-wrap',
                    }}>
                      {msg.text}
                    </div>
                    {msg.exportRows?.length > 0 && (
                      <button onClick={() => downloadCsv(msg.exportRows)} style={{
                        alignSelf: 'flex-start', background: 'none', border: '1px solid var(--gray-300)',
                        borderRadius: 6, padding: '3px 9px', fontSize: 11, color: 'var(--gray-600)',
                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                      }}>
                        ↓ Export CSV ({msg.exportRows.length} rows)
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {chatLoading && (
                <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', gap: 8 }}>
                  <img src={jimmyImg} alt="Jimmy" style={{ width: 28, height: 28, objectFit: 'contain', flexShrink: 0 }} />
                  <div style={{ padding: '8px 14px', borderRadius: '12px 12px 12px 2px', background: 'var(--gray-50)', border: '1px solid var(--gray-100)', fontSize: 18, letterSpacing: 2, color: 'var(--gray-400)' }}>
                    ···
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Input */}
            <div style={{ display: 'flex', gap: 8, padding: '10px 16px', borderTop: '1px solid var(--gray-100)' }}>
              <input
                type="text"
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendChat()}
                placeholder="Ask about VIP members..."
                className="form-input"
                style={{ flex: 1, fontSize: 12 }}
                disabled={chatLoading}
              />
              <button onClick={sendChat} disabled={chatLoading || !chatInput.trim()} className="btn btn-primary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                Send
              </button>
              {displayMessages.length > 0 && (
                <button onClick={() => { setDisplayMessages([]); setApiMessages([]); }} className="btn btn-navy" style={{ fontSize: 12 }}>
                  Clear
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── CITY MEMBERS TOOLTIP ── */}
      {cityMembersTip && (<>
        <div style={{ position: 'fixed', inset: 0, zIndex: 9998 }} onClick={() => setCityMembersTip(null)} />
        <div id="city-members-tip" style={{
          position: 'fixed',
          left: Math.min(cityMembersTip.x, window.innerWidth - 240),
          top: cityMembersTip.y,
          zIndex: 9999,
          background: '#1e293b',
          color: '#fff',
          fontSize: 11,
          borderRadius: 7,
          padding: '8px 12px',
          minWidth: 200,
          maxWidth: 280,
          maxHeight: 260,
          overflowY: 'auto',
          boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
        }}>
          <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 6, borderBottom: '1px solid rgba(255,255,255,0.15)', paddingBottom: 4 }}>
            {toTitleCase(cityMembersTip.city)}, {cityMembersTip.state}
          </div>
          {cityMembersTip.loading && <div style={{ color: 'rgba(255,255,255,0.5)' }}>Loading…</div>}
          {!cityMembersTip.loading && (!cityMembersTip.members || cityMembersTip.members.length === 0) && (
            <div style={{ color: 'rgba(255,255,255,0.5)' }}>No members found</div>
          )}
          {!cityMembersTip.loading && cityMembersTip.members && cityMembersTip.members.map(m => (
            <div key={m.id} style={{ padding: '3px 0', borderBottom: '1px solid rgba(255,255,255,0.07)', lineHeight: 1.4 }}>
              <div style={{ fontWeight: 600 }}>{toTitleCase(m.first_name)} {toTitleCase(m.last_name)}</div>
              <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 10 }}>{m.email}</div>
            </div>
          ))}
        </div>
      </>)}
    </div>
  );
}
